import {
  Component,
  DestroyRef,
  ElementRef,
  HostListener,
  PLATFORM_ID,
  ViewChild,
  afterNextRender,
  computed,
  inject,
  signal,
} from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ProductCard } from '../../../shared/product-card/product-card';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { Subject, catchError, debounceTime, distinctUntilChanged, of, switchMap } from 'rxjs';
import { SavingArenaApiService } from '../../../core/api/saving-arena-api.service';
import { SAVING_ARENA_API_CONFIG } from '../../../core/api/api.config';
import {
  FrontendDealsStore,
  FrontendDealsStoreNavItem,
  FrontendDealsStoreEditorial,
  FrontendDealsStoreBottom,
  FrontendDealsStoreVideo,
  FrontendDealsStoreRelated,
  FrontendProduct,
} from '../../../core/api/api.models';
import { SeoService } from '../../../core/seo/seo.service';
import { formatCompactCount } from '../../../core/format/compact-count';
import { SsrResponseService } from '../../../core/seo/ssr-response.service';
import { breadcrumbSchemaFor, breadcrumbTrail } from '../../../core/seo/breadcrumb-trail';
import { webPageSchema } from '../../../core/seo/structured-data';
import { Breadcrumb } from '../../../shared/breadcrumb/breadcrumb';
import { StoreLogo } from '../../../shared/store-logo/store-logo';

/** Section element id derived from a nav key — must match on both the build and scroll sides. */
function toSectionId(navKey: string): string {
  return navKey.replace(/[^a-z0-9]+/gi, '-');
}

interface ProductSection {
  /** Stable id used to update this section in place. */
  id: string;
  heading: string;
  /** Query for the products endpoint: a nav key and/or a sort. */
  nav: string;
  sort: 'discount' | 'newest';
  products: FrontendProduct[];
  total: number;
  page: number;
  loading: boolean;
  /** Whether this section's first page has been fetched yet. */
  loaded: boolean;
  /**
   * Eager sections (the discounted strip + new arrivals) load on page init so the top of the page is
   * server-rendered. The rest load lazily when scrolled near — keeping the initial DOM and request
   * count small on a page that can otherwise render 100+ cards up front.
   */
  eager: boolean;
}

// Products per request. The API caps `limit` at 60; 24 keeps each load quick and the grid tidy,
// with "Load more" walking through the rest of the catalogue.
const PRODUCTS_PER_PAGE = 24;

@Component({
  selector: 'app-deals-store-page',
  imports: [Breadcrumb, ProductCard, RouterLink, StoreLogo],
  templateUrl: './deals-store-page.html',
  styleUrl: './deals-store-page.scss',
})
export class DealsStorePage {
  private readonly route = inject(ActivatedRoute);
  private readonly seo = inject(SeoService);
  private readonly ssrResponse = inject(SsrResponseService);
  private readonly api = inject(SavingArenaApiService);
  private readonly apiConfig = inject(SAVING_ARENA_API_CONFIG);
  private readonly destroyRef = inject(DestroyRef);
  private readonly isBrowser = isPlatformBrowser(inject(PLATFORM_ID));
  private readonly router = inject(Router);
  private readonly sanitizer = inject(DomSanitizer);

  // Offset for the sticky nav = height of the site header it tucks under. 128 is the desktop header
  // height, used until the real value is measured on the client (and during SSR).
  protected readonly stickyNavTop = signal(128);

  // How far a section must clear when scrolled to, so it lands below both sticky bars (header + nav)
  // instead of behind them. Applied as scroll-margin-top on each section.
  protected readonly sectionScrollMargin = signal(190);
  @ViewChild('storeSearchInput') private readonly storeSearchInput?: ElementRef<HTMLInputElement>;
  @ViewChild('storeNav') private readonly storeNav?: ElementRef<HTMLElement>;
  @ViewChild('relatedTrack') private readonly relatedTrack?: ElementRef<HTMLElement>;

  private readonly fallbackStoreSlug = 'zaful.com';
  private readonly fallbackStoreName = 'Zaful';

  // A signal so the page reacts to route-param changes. Angular reuses this component when only the
  // param changes (e.g. clicking a related-store link), so reading the snapshot once would leave the
  // old store on screen — see the paramMap subscription in the constructor.
  protected readonly storeSlug = signal(
    this.route.snapshot.paramMap.get('storeName')?.trim() || this.fallbackStoreSlug,
  );

  // Live store record behind this page, loaded from the route param. Null until it resolves (and if
  // the store does not exist), which is why every derived signal keeps a local fallback.
  private readonly dealsStore = signal<FrontendDealsStore | null>(null);

  protected readonly crumbs = computed(() => {
    const name = this.storeName();
    return breadcrumbTrail(
      { label: 'Deals', path: '/collection' },
      ...(name ? [{ label: name }] : []),
    );
  });

  protected readonly storeName = computed(
    () => this.dealsStore()?.store.name || this.toDisplayName(this.storeSlug()),
  );

  // Where "Visit Official … Store" goes. Always the Store record's affiliate link — this page never
  // stores its own copy — so it stays empty (and the button stays disabled) until the store loads.
  protected readonly affiliateLink = computed(() => this.dealsStore()?.store.affiliateLink || '');

  protected readonly visitButtonLabel = computed(
    () => this.dealsStore()?.hero?.buttonText || `Visit Official ${this.storeName()} Store`,
  );

  protected readonly isSearchOpen = signal(false);

  // Nav tabs come from the API: the feed's own categories plus any admin-authored ones. The "All"
  // tab is added here rather than by the API — it is a view concern, not data.
  protected readonly navTabs = computed<FrontendDealsStoreNavItem[]>(() => {
    const store = this.dealsStore();
    if (!store) return [];

    return [
      {
        key: '',
        label: 'All Products',
        productCount: store.store.productCount,
        source: 'feed' as const,
      },
      ...store.nav,
    ];
  });

  /**
   * The page is a stack of product sections rather than one filtered grid: a discounted strip, a
   * new-arrivals strip, then one section per navigation tab. Each keeps its own paging so "load
   * more" in one does not disturb the others.
   */
  protected readonly sections = signal<ProductSection[]>([]);

  /**
   * Which section the editorial collage renders under: the discounted strip when it has products,
   * otherwise new arrivals. So a store with no discounted stock shows the collage below New Arrivals
   * rather than above it.
   */
  protected readonly collageAfterSectionId = computed(() => {
    const discounted = this.sections().find((section) => section.id === 'discounted');

    return discounted && discounted.products.length > 0 ? 'discounted' : 'new-arrivals';
  });

  // Store search: while a term is active the section stack is hidden and a single results grid takes
  // its place. Debounced through a Subject so we query only after the user pauses typing.
  private readonly searchInput$ = new Subject<string>();
  protected readonly searchTerm = signal('');
  protected readonly searchResults = signal<FrontendProduct[]>([]);
  protected readonly searchTotal = signal(0);
  protected readonly isSearching = signal(false);
  protected readonly isSearchActive = computed(() => this.searchTerm().trim().length >= 2);

  // (static demo collections removed — the lookbook is admin-managed now)

  // Empty when no banner has been uploaded for this store. The hero then renders the logo alone
  // rather than borrowing artwork that belongs to a different brand.
  protected readonly bannerImageUrl = computed(() =>
    this.assetUrl(this.dealsStore()?.hero?.bannerImagePath ?? ''),
  );

  protected readonly bannerAltText = computed(
    () => this.dealsStore()?.hero?.bannerAltText || `${this.storeName()} banner`,
  );

  // Admin-managed lookbook: full-width banner, half-width hero, and a four-tile collage. Null when
  // the store has none set, in which case the storefront drops the whole section.
  protected readonly editorial = computed<FrontendDealsStoreEditorial | null>(() => {
    const data = this.dealsStore()?.editorial;
    if (!data) return null;

    return {
      full: data.full ? { imageUrl: this.assetUrl(data.full.imageUrl), url: data.full.url } : null,
      fullHeading: data.fullHeading,
      half: data.half ? { imageUrl: this.assetUrl(data.half.imageUrl), url: data.half.url } : null,
      collage: data.collage.map((item) => ({
        imageUrl: this.assetUrl(item.imageUrl),
        url: item.url,
      })),
      collageHeading: data.collageHeading,
    };
  });

  // Second admin-managed lookbook (full-width banner, a two-up row, a three-tile row) and the
  // campaign video, both null when the store has none set.
  protected readonly isCampaignVideoPlaying = signal(false);

  // Related-category stores that also have a deals page. Loaded separately so it never blocks the
  // rest of the page. Empty (section hidden) when the store's category has no other qualifying store.
  protected readonly relatedStores = signal<{ name: string; path: string; logoUrl: string }[]>([]);

  protected readonly bottom = computed<FrontendDealsStoreBottom | null>(() => {
    const data = this.dealsStore()?.bottom;
    if (!data) return null;

    const map = (item: { imageUrl: string; url: string }) => ({
      imageUrl: this.assetUrl(item.imageUrl),
      url: item.url,
    });

    return {
      full: data.full ? map(data.full) : null,
      fullHeading: data.fullHeading,
      duo: data.duo.map(map),
      duoHeading: data.duoHeading,
      trio: data.trio.map(map),
      trioHeading: data.trioHeading,
    };
  });

  protected readonly video = computed<FrontendDealsStoreVideo | null>(
    () => this.dealsStore()?.video ?? null,
  );

  // Only built once the user clicks play, so YouTube is not loaded on page view. autoplay=1 starts
  // it immediately since the click is the user gesture browsers require.
  protected readonly videoEmbedUrl = computed<SafeResourceUrl | null>(() => {
    const data = this.video();
    if (!data) return null;

    return this.sanitizer.bypassSecurityTrustResourceUrl(`${data.embedUrl}?autoplay=1&rel=0`);
  });

  // Always the store's own logo — admins never upload one here.
  protected readonly logoImageUrl = computed(() =>
    this.assetUrl(this.dealsStore()?.store.logoImagePath ?? ''),
  );

  // Cross-link to this store's coupon page. Every store has one, so this only waits on the load.
  protected readonly couponPagePath = computed(() => {
    const slug = (this.dealsStore()?.store.slug ?? '').trim();

    return slug ? `/${slug}` : '';
  });

  /**
   * Follow / Share badge figures.
   *
   * They come from the API rather than being held here, which is the whole point: this page and the
   * store's coupon page are the same store, and the numbers were "24.6K" and "3.2K" typed into the
   * template — the same two figures on every store on the site, and different from the real ones the
   * coupon page showed for the same brand.
   *
   * `shareCount` is a signal rather than a computed because a completed share updates it from the
   * count the API returns, so the badge moves without a reload and never drifts from what is stored.
   */
  protected readonly followerCount = computed(() =>
    Number(this.dealsStore()?.store.followers ?? 0),
  );
  protected readonly shareCount = signal(0);
  protected readonly shareLabel = signal('Share');

  /** 24600 -> "24.6K". Shared with the store page so both render the same figure identically. */
  protected formatCount(value: number): string {
    return formatCompactCount(value);
  }

  /**
   * Share this store: the OS share sheet where there is one, the clipboard otherwise. Counted
   * server-side, and the badge takes the figure the API returns rather than incrementing locally.
   *
   * Dismissing the sheet raises `AbortError` — that is not a share and must not count.
   */
  protected async shareStore(): Promise<void> {
    const slug = (this.dealsStore()?.store.slug ?? '').trim();
    if (!slug || !this.isBrowser) return;

    const url = `${window.location.origin}/${slug}`;

    try {
      if (navigator.share) {
        await navigator.share({ title: this.storeName(), url });
      } else {
        await navigator.clipboard.writeText(url);
        this.shareLabel.set('Link copied');
      }
    } catch (error) {
      if ((error as DOMException)?.name === 'AbortError') return;
      this.shareLabel.set('Copy failed');
      setTimeout(() => this.shareLabel.set('Share'), 2000);
      return;
    }

    setTimeout(() => this.shareLabel.set('Share'), 2000);

    this.api
      .shareStore(slug)
      .pipe(
        catchError(() => of(null)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((response) => {
        const shares = response?.data?.shares;
        if (typeof shares === 'number') this.shareCount.set(shares);
      });
  }

  constructor() {
    this.wireSearch();

    // Re-run for every store the route points at, including navigations that reuse this component
    // (clicking a related-store link). paramMap emits the current value immediately on subscribe, so
    // this also handles the first load.
    this.route.paramMap.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((params) => {
      this.storeSlug.set(params.get('storeName')?.trim() || this.fallbackStoreSlug);
      this.onStoreChanged();
    });

    afterNextRender(() => this.measureHeaderHeight());
  }

  // Clears the previous store's content and loads the new one. Without the reset, a slower response
  // or a section the new store lacks would leave stale content on screen.
  private onStoreChanged(): void {
    this.dealsStore.set(null);
    this.sections.set([]);
    this.relatedStores.set([]);
    this.searchTerm.set('');
    this.searchResults.set([]);
    this.searchTotal.set(0);
    this.isSearchOpen.set(false);

    this.applySeo();
    this.loadDealsStore();
  }

  @HostListener('window:resize')
  protected onWindowResize(): void {
    this.measureHeaderHeight();
  }

  private measureHeaderHeight(): void {
    if (!this.isBrowser) return;

    const header = document.querySelector('header');
    const headerHeight = header ? Math.round(header.getBoundingClientRect().height) : 128;
    const navHeight = this.storeNav
      ? Math.round(this.storeNav.nativeElement.getBoundingClientRect().height)
      : 62;

    this.stickyNavTop.set(headerHeight);
    this.sectionScrollMargin.set(headerHeight + navHeight);
  }

  private loadDealsStore(): void {
    const slug = this.storeSlug();

    this.api
      .getDealsStore(slug)
      .pipe(
        catchError((error: unknown) => {
          this.ssrResponse.noteFetchFailure(error);
          return of(null);
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((response) => {
        // A response for a store the user has already navigated away from must not overwrite the
        // current one.
        if (this.storeSlug() !== slug) return;

        const data = response?.data ?? null;
        if (!data) {
          // Returning here used to leave the page as it was: `applySeo` was never reached, so the
          // title, H1 and canonical stayed the ones derived from the slug and the URL answered 200.
          // /collection/<anything>/<anything> therefore rendered as an indexable page for any slug
          // a crawler cared to invent.
          this.ssrResponse.setNotFound();
          this.seo.apply({
            title: 'Store not found | SavingArena',
            description: 'This store has no deals collection on SavingArena.',
            noindex: true,
          });
          this.seo.setJsonLd([]);
          return;
        }

        this.dealsStore.set(data);
        // Seeded from the payload so the badge is right on first paint; a completed share then moves
        // it from the figure the API returns.
        this.shareCount.set(Number(data.store?.shares ?? 0));
        this.canonicalisePath(data);
        this.applySeo();
        this.buildSections(data);
        this.loadRelatedStores();
      });
  }

  /**
   * Rewrites the address to `/collection/<categorySlug>/<storeKey>` when it isn't already.
   *
   * This no longer has `/deals/:storeName` to correct — that route is retired. What it still catches
   * is a link carrying a STALE collection: a store that moves category keeps working links at its
   * old `/collection/<oldCategory>/<store>` address, and the collection is only known once the store
   * loads, so it cannot be fixed by a route. `replaceUrl` keeps the correction out of the back
   * history, so Back still leaves the page.
   */
  private canonicalisePath(data: FrontendDealsStore): void {
    if (!this.isBrowser) return;

    const categorySlug = (data.store?.categorySlug ?? '').trim();
    const storeKey = (data.store?.storeKey ?? '').trim() || this.storeSlug();
    if (!categorySlug || !storeKey) return; // no collection to name — leave the URL alone

    const target = `/collection/${categorySlug}/${storeKey}`;
    if (this.router.url.split('?')[0].split('#')[0] === target) return;

    void this.router.navigateByUrl(target, { replaceUrl: true });
  }

  /** Nav tabs scroll to their section rather than filtering, now that every tab has one. */
  protected scrollToNavSection(navKey: string): void {
    const id = toSectionId(navKey);
    const section = this.sectionById(id);
    if (section && !section.loaded && !section.loading) this.loadSection(id, 1);
    this.scrollToSection(id);
  }

  protected scrollToSection(sectionId: string): void {
    document
      .getElementById(`deals-section-${sectionId}`)
      ?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  protected loadMore(section: ProductSection): void {
    if (section.loading || section.products.length >= section.total) return;

    this.loadSection(section.id, section.page + 1);
  }

  protected hasMore(section: ProductSection): boolean {
    return section.products.length < section.total;
  }

  /**
   * Id of the nth navigation-tab section — the category/custom tabs, excluding the discounted and
   * new-arrivals strips. Used to drop one bottom-section image group under each nav tab's products
   * (full under tab 1, the two-up under tab 2, the three-tile under tab 3). '' when there is no
   * tab at that ordinal, so that group simply does not render.
   */
  protected navTabSectionId(ordinal: number): string {
    const navTabs = this.sections().filter(
      (section) => section.id !== 'discounted' && section.id !== 'new-arrivals',
    );

    return navTabs[ordinal]?.id ?? '';
  }

  /**
   * Builds the section list once the store is known: the two derived strips first, then one per
   * navigation tab. Each section then loads its own first page.
   */
  private buildSections(store: FrontendDealsStore): void {
    const sections: ProductSection[] = [
      {
        id: 'discounted',
        heading: `${store.store.name} Recommended Discounted Products For You`,
        nav: '',
        sort: 'discount',
        products: [],
        total: 0,
        page: 1,
        loading: true,
        loaded: false,
        eager: true,
      },
      {
        id: 'new-arrivals',
        heading: 'New Arrivals',
        nav: '',
        sort: 'newest',
        products: [],
        total: 0,
        page: 1,
        loading: true,
        loaded: false,
        eager: true,
      },
      ...store.nav.map((tab) => ({
        id: toSectionId(tab.key),
        heading: tab.label,
        nav: tab.key,
        sort: 'newest' as const,
        products: [],
        total: 0,
        page: 1,
        loading: false,
        loaded: false,
        eager: false,
      })),
    ];

    this.sections.set(sections);
    // Only the eager sections fetch now; the lazy ones wait until they scroll near the viewport.
    sections
      .filter((section) => section.eager)
      .forEach((section) => this.loadSection(section.id, 1));
    this.observeLazySections();
  }

  private lazyObserver?: IntersectionObserver;

  // Watches the placeholder of each not-yet-loaded lazy section and loads it just before it scrolls
  // into view. Client-only; on the server the placeholders render and the client takes over.
  private observeLazySections(): void {
    if (!this.isBrowser) return;

    this.lazyObserver?.disconnect();
    this.lazyObserver = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (!entry.isIntersecting) continue;
          const id = (entry.target as HTMLElement).dataset['lazySection'];
          this.lazyObserver?.unobserve(entry.target);
          if (id) this.loadSection(id, 1);
        }
      },
      // Start loading ~500px before the section reaches the viewport, so it is ready on arrival.
      { rootMargin: '500px 0px' },
    );

    // Sections render on the next change-detection pass; observe once the DOM has them.
    setTimeout(() => {
      document
        .querySelectorAll('[data-lazy-section]')
        .forEach((el) => this.lazyObserver?.observe(el));
    });
  }

  /** Page 1 replaces a section's grid; later pages append to it. */
  private loadSection(sectionId: string, page: number): void {
    const section = this.sections().find((item) => item.id === sectionId);
    if (!section) return;

    this.patchSection(sectionId, { loading: true });

    this.api
      .getDealsStoreProducts(this.storeSlug(), {
        nav: section.nav || undefined,
        sort: section.sort === 'discount' ? 'discount' : undefined,
        page,
        limit: PRODUCTS_PER_PAGE,
      })
      .pipe(
        catchError(() => of(null)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((response) => {
        const rows = response?.data ?? [];

        this.patchSection(sectionId, {
          products: page === 1 ? rows : [...(this.sectionById(sectionId)?.products ?? []), ...rows],
          total: response?.pagination?.total ?? 0,
          page,
          loading: false,
          loaded: true,
        });
      });
  }

  private sectionById(sectionId: string): ProductSection | undefined {
    return this.sections().find((item) => item.id === sectionId);
  }

  private patchSection(sectionId: string, patch: Partial<ProductSection>): void {
    this.sections.update((sections) =>
      sections.map((section) => (section.id === sectionId ? { ...section, ...patch } : section)),
    );
  }

  protected productPrice(product: FrontendProduct): string {
    return String(product.newPrice ?? '').trim();
  }

  protected productOldPrice(product: FrontendProduct): string {
    return String(product.oldPrice ?? '').trim();
  }

  protected productImage(product: FrontendProduct): string {
    return this.assetUrl(String(product.thumbnailImagePath ?? ''));
  }

  // First gallery angle that genuinely differs from the thumbnail; '' when the feed gave only one.
  protected productHoverImage(product: FrontendProduct): string {
    const mainImagePath = String(product.thumbnailImagePath ?? '');
    const alternate = (product.galleryImagePaths ?? []).find(
      (path) => path && path !== mainImagePath,
    );

    return alternate ? this.assetUrl(String(alternate)) : '';
  }

  protected productIsExternal(product: FrontendProduct): boolean {
    return product.isExternal === true;
  }

  /** In-app product page, for the card's "View Detail" action. Falls back to the id when unslugged. */
  protected productDetailHref(product: FrontendProduct): string {
    const slug = String(product['slug'] ?? '').trim();
    const key = slug || String(product.id ?? '').trim();

    return key ? `/${key}` : '';
  }

  // Re-run once the store loads so the title uses its real name ("Zaful") rather than the name
  // derived from the URL ("Zaful Com").
  private applySeo(): void {
    const name = this.storeName();
    const categorySlug = (this.dealsStore()?.store.categorySlug ?? '').trim();

    // Without a category there is no `/collection/<cat>/<store>` URL to point at, and the old
    // fallback pointed at `/deals/<store>` — a route that answers 410 Gone (see app.routes.ts). A
    // canonical naming a dead URL tells Google the page it just fetched is not the one to index,
    // and then hands it one that no longer exists, so neither gets indexed.
    //
    // Self-referential instead: SeoService falls back to the router URL when no canonicalPath is
    // given, which is what the other 47 pages do and what this page should always have done.
    const path = categorySlug ? `/collection/${categorySlug}/${this.storeSlug()}` : this.router.url;
    const url = this.seo.absolute(path);
    const title = `${name} Deals & Product Offers | SavingArena`;
    const description = `Shop the latest ${name} deals, collections and product offers curated by SavingArena.`;

    this.seo.apply({ title, description, canonicalPath: path });
    this.seo.setJsonLd([
      webPageSchema({ origin: this.seo.origin, url, name: title, description }),
      breadcrumbSchemaFor(this.crumbs(), (p) => this.seo.absolute(p)),
    ]);
  }

  private assetUrl(path: string): string {
    const value = (path ?? '').trim();
    if (!value) return '';
    if (/^https?:\/\//i.test(value)) return value;

    const base = (this.apiConfig.assetBaseUrl ?? '').replace(/\/$/, '');
    return `${base}${value.startsWith('/') ? value : `/${value}`}`;
  }

  // Swap the thumbnail for the YouTube iframe. One-way: there is no inline "stop" for an embed.
  private loadRelatedStores(): void {
    const slug = this.storeSlug();

    this.api
      .getDealsStoreRelated(slug, 12)
      .pipe(
        catchError(() => of(null)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((response) => {
        if (this.storeSlug() !== slug) return;

        this.relatedStores.set(
          (response?.data ?? [])
            .filter((item: FrontendDealsStoreRelated) => item.storeKey)
            .map((item: FrontendDealsStoreRelated) => ({
              name: item.name,
              path: item.categorySlug
                ? `/collection/${item.categorySlug}/${item.storeKey}`
                : `/deals/${item.storeKey}`,
              logoUrl: this.assetUrl(item.logoImagePath),
            })),
        );
      });
  }

  // Nudges the related-stores carousel left/right by roughly one card group.
  protected scrollRelated(direction: number): void {
    const track = this.relatedTrack?.nativeElement;
    if (track) {
      track.scrollBy({ left: direction * 320, behavior: 'smooth' });
    }
  }

  protected playCampaignVideo(): void {
    this.isCampaignVideoPlaying.set(true);
  }

  protected onSearchInput(event: Event): void {
    const value = (event.target as HTMLInputElement).value;
    this.searchTerm.set(value);
    this.searchInput$.next(value);
  }

  private wireSearch(): void {
    this.searchInput$
      .pipe(
        debounceTime(300),
        distinctUntilChanged(),
        switchMap((term) => {
          const trimmed = term.trim();
          if (trimmed.length < 2) {
            this.isSearching.set(false);
            return of(null);
          }

          this.isSearching.set(true);
          return this.api
            .getDealsStoreProducts(this.storeSlug(), { search: trimmed, limit: 60 })
            .pipe(catchError(() => of(null)));
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((response) => {
        // A response that arrived after the user cleared the box must not resurrect the results view.
        if (!this.isSearchActive()) {
          this.searchResults.set([]);
          this.searchTotal.set(0);
          return;
        }

        this.searchResults.set(response?.data ?? []);
        this.searchTotal.set(response?.pagination?.total ?? 0);
        this.isSearching.set(false);
      });
  }

  protected closeSearch(): void {
    this.isSearchOpen.set(false);
    // Closing the search box clears the term so the section stack comes back.
    this.searchTerm.set('');
    this.searchInput$.next('');
    this.searchResults.set([]);
    this.searchTotal.set(0);
  }

  protected toggleSearch(): void {
    const nextValue = !this.isSearchOpen();

    this.isSearchOpen.set(nextValue);

    if (nextValue) {
      setTimeout(() => this.storeSearchInput?.nativeElement.focus());
    }
  }

  @HostListener('document:keydown.escape')
  protected handleEscapeKey(): void {
    this.closeSearch();
  }

  private toDisplayName(value: string): string {
    const sanitized = value.replace(/[._]+/g, ' ').replace(/-/g, ' ').trim();

    if (!sanitized) {
      return this.fallbackStoreName;
    }

    return sanitized.replace(/\b\w/g, (char) => char.toUpperCase());
  }
}
